import React, { useState, useEffect } from "react";
import { X, Plus } from "lucide-react";

export default function CustomerFormModal({ customer, onClose, onSaved }) {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");

  const [form, setForm] = useState({
    username: "",
    password: "",
    role: "customer",
    alarm_emails: [],
    contact_phone: "",
    description: "",
  });

  const [newEmail, setNewEmail] = useState("");
  const [error, setError] = useState(""); // hata mesajı için state

  // Edit modunda müşteri bilgilerini doldur
  useEffect(() => {
    if (customer) {
      let emails = [];

      // alarm_emails varsa kullan
      if (customer.alarm_emails && Array.isArray(customer.alarm_emails)) {
        emails = customer.alarm_emails.filter(e => e && e.trim());
      }
      // contact_email varsa fallback olarak kullan
      else if (customer.contact_email) {
        emails = [customer.contact_email];
      }

      setForm({
        username: customer.username || "",
        role: customer.role || "customer",
        alarm_emails: emails,
        contact_phone: customer.contact_phone || "",
        description: customer.description || "",
      });
      setNewEmail("");
    }
  }, [customer]);

  function validateForm() {
    if (!form.username.trim()) return "Kullanıcı adı gerekli.";
    if (!customer && !form.password.trim())
      return "Yeni müşteri eklerken şifre gerekli.";
    if (!form.description.trim()) return "Açıklama gerekli.";

    // Validate all emails (filter out empty strings)
    const validEmails = form.alarm_emails.filter(e => e && e.trim());
    for (const email of validEmails) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return `Geçersiz e-posta adresi: ${email}`;
      }
    }

    if (form.contact_phone && !/^[0-9+\s-]+$/.test(form.contact_phone)) {
      return "Telefon numarası yalnızca rakam, boşluk, + ve - içerebilir.";
    }

    return null; // hata yok
  }

  function addEmail() {
    if (!newEmail.trim()) {
      setError("E-posta adresi boş olamaz.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setError("Geçerli bir e-posta adresi giriniz.");
      return;
    }
    if (form.alarm_emails.includes(newEmail.trim())) {
      setError("Bu e-posta adresi zaten ekli.");
      return;
    }
    const updatedEmails = [...form.alarm_emails, newEmail.trim()];
    setForm({ ...form, alarm_emails: updatedEmails });
    console.log("✅ Email added:", updatedEmails);
    setNewEmail("");
    setError("");
  }

  function removeEmail(index) {
    setForm({
      ...form,
      alarm_emails: form.alarm_emails.filter((_, i) => i !== index),
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    const method = customer ? "PUT" : "POST";
    const url = customer
      ? `${API_BASE}/customers/${customer.id}`
      : `${API_BASE}/customers`;

    // Filter out empty emails before sending
    const submitData = {
      ...form,
      alarm_emails: form.alarm_emails.filter(e => e && e.trim()),
    };

    console.log("📤 Sending data to backend:", submitData);

    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(submitData),
      });

      console.log("📥 Response status:", res.status);
      const responseData = await res.json();
      console.log("📥 Response data:", responseData);

      if (!res.ok) throw new Error("HTTP " + res.status);

      await onSaved();
      onClose();
    } catch (err) {
      console.error("❌ Save customer error:", err);
      setError("Kaydetme sırasında bir hata oluştu. Tekrar deneyin.");
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6">
        <h3 className="text-lg font-semibold mb-4">
          {customer ? "Müşteriyi Düzenle" : "Yeni Müşteri Ekle"}
        </h3>

        {error && (
          <div className="mb-4 text-sm text-red-600 bg-red-100 p-2 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Kullanıcı adı *"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            className="w-full border rounded-md px-3 py-2"
          />
          {!customer && (
            <input
              type="password"
              placeholder="Şifre *"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full border rounded-md px-3 py-2"
            />
          )}

          {/* Multiple Emails Section */}
          <div className="border rounded-md p-3 bg-gray-50">
            <label className="block text-sm font-medium mb-2 text-gray-700">
              Alarm E-Postaları
            </label>

            {/* Email List */}
            {form.alarm_emails.length > 0 && (
              <div className="mb-3 space-y-2">
                {form.alarm_emails.map((email, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-white border rounded-md px-3 py-2"
                  >
                    <span className="text-sm text-gray-700">{email}</span>
                    <button
                      type="button"
                      onClick={() => removeEmail(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add Email Input */}
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="Yeni e-posta ekle"
                value={newEmail}
                onChange={(e) => {
                  setNewEmail(e.target.value);
                  setError("");
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addEmail();
                  }
                }}
                className="flex-1 border rounded-md px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={addEmail}
                className="bg-indigo-500 text-white px-3 py-2 rounded-md hover:bg-indigo-600 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Ekle
              </button>
            </div>
          </div>
          <input
            type="text"
            placeholder="Telefon"
            value={form.contact_phone}
            onChange={(e) =>
              setForm({ ...form, contact_phone: e.target.value })
            }
            className="w-full border rounded-md px-3 py-2"
          />
          <textarea
            placeholder="Açıklama *"
            value={form.description}
            onChange={(e) =>
              setForm({ ...form, description: e.target.value })
            }
            className="w-full border rounded-md px-3 py-2"
          />
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300"
            >
              İptal
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-md bg-indigo-500 text-white hover:bg-indigo-600"
            >
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
